import { Injectable } from '@angular/core';
import {
  TREQUEST_ADD_EDIT_FACTORY_HOLIDAYS,
  TREQUEST_ADD_EDIT_OPERATOR_COUNT,
  TREQUEST_ADD_EDIT_SHIFT,
  TREQUEST_ADD_EDIT_WORKFORCE_COUNT,
  TREQUEST_ADD_EDIT_WORKING_HOURS,
  TREQUEST_DELETE_FACTORY_HOLIDAYS,
  TREQUEST_FACTORY_SHIFT,
  TREQUEST_RESET_COUNT,
  TREQUEST_UPDATE_DEFAULT_WORKFORCE_COUNT,
  TREQUEST_UPDATE_DEFAULT_WORKING_HOURS_REQUEST,
  TREQUEST_WORKFORCE_RESET_TO_DEFAULT,
} from '@monorepo/types';
import { select, Store } from '@ngrx/store';
import { TPlani8State } from '../reducers';
import * as FactoryShiftActions from './factory-shift.actions';
import * as FactoryShiftSelectors from './factory-shift.selectors';
import { TShiftCategoryTab } from './factory-shift.models';
import { lastValueFrom } from 'rxjs';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';

@Injectable({
  providedIn: 'root',
})
export class FactoryShiftFacade {
  constructor(
    private store: Store<TPlani8State>,
    private appNotificationUtilClass: AppNotificationUtilClass,
    private plani8DataService: Plani8DataService,
  ) {}

  setCurrentShiftTab(currentTab: TShiftCategoryTab) {
    this.store.dispatch(new FactoryShiftActions.SetShiftCategoryTab(currentTab));
  }

  fetchFactoryShiftInfo(factoryId: string, payload: TREQUEST_FACTORY_SHIFT) {
    this.store.dispatch(new FactoryShiftActions.GetFactoryShiftInfo(factoryId, payload));
  }

  addEditOperatorCount(factoryId: string, payload: TREQUEST_ADD_EDIT_OPERATOR_COUNT, shiftId: string) {
    this.store.dispatch(new FactoryShiftActions.AddEditOperatorCount(factoryId, payload, shiftId));
  }

  addEditWorkingHours(factoryId: string, payload: TREQUEST_ADD_EDIT_WORKING_HOURS) {
    this.store.dispatch(new FactoryShiftActions.AddEditWorkingHours(factoryId, payload));
  }

  resetOperatorCount(factoryId: string, payload: TREQUEST_RESET_COUNT) {
    this.store.dispatch(new FactoryShiftActions.ResetOperatorCount(factoryId, payload));
  }

  resetWorkingHours(factoryId: string, payload: TREQUEST_RESET_COUNT) {
    this.store.dispatch(new FactoryShiftActions.ResetWorkingHours(factoryId, payload));
  }

  fetchFactoryHolidays(factoryId: string) {
    this.store.dispatch(new FactoryShiftActions.GetFactoryHolidayInfo(factoryId));
  }

  addEditFactoryHolidays(factoryId: string, payload: TREQUEST_ADD_EDIT_FACTORY_HOLIDAYS) {
    this.store.dispatch(new FactoryShiftActions.AddEditFactoryHoliday(factoryId, payload));
  }

  deleteFactoryHolidays(factoryId: string, payload: TREQUEST_DELETE_FACTORY_HOLIDAYS) {
    this.store.dispatch(new FactoryShiftActions.DeleteFactoryHoliday(factoryId, payload));
  }

  updateDefaultWorkforceCountRequest(payload: TREQUEST_UPDATE_DEFAULT_WORKFORCE_COUNT) {
    this.store.dispatch(new FactoryShiftActions.UpdateDefaultWorkforceCountRequest(payload));
  }

  updateDefaultWorkingHoursRequest(payload: TREQUEST_UPDATE_DEFAULT_WORKING_HOURS_REQUEST, factoryId: string) {
    this.store.dispatch(new FactoryShiftActions.UpdateDefaultWorkingHoursRequest(payload, factoryId));
  }

  addEditWorkForceCount(payload: TREQUEST_ADD_EDIT_WORKFORCE_COUNT, factoryId: string) {
    this.store.dispatch(new FactoryShiftActions.AddEditWorkForceCount(payload, factoryId));
  }

  workForceResetToDefault(payload: TREQUEST_WORKFORCE_RESET_TO_DEFAULT, factoryId: string) {
    this.store.dispatch(new FactoryShiftActions.WorkForceResetToDefault(payload, factoryId));
  }

  clearFactoryShiftInfo() {
    this.store.dispatch(new FactoryShiftActions.ClearFactoryShiftInfo());
  }

  async getOrgHierarcy(factoryId: string) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(this.plani8DataService.getOrgHierarchy(factoryId)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async getAllShiftsByFactory(factoryId: string) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(this.plani8DataService.getAllShiftsByFactory(factoryId)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async getAllShifts() {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(this.plani8DataService.getAllShifts()).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async addEditShift(payload: TREQUEST_ADD_EDIT_SHIFT) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(this.plani8DataService.addEditShift(payload)).finally(() => {
      this.appNotificationUtilClass.dismissLoadingV2(loading);
    });
  }

  async getShiftById(shiftId: string) {
    const loading = await this.appNotificationUtilClass.presentLoading('Loading', 3000);
    return lastValueFrom(this.plani8DataService.getShiftById(shiftId)).finally(() =>
      this.appNotificationUtilClass.dismissLoadingV2(loading),
    );
  }

  getFactoryShiftInfo() {
    return this.store.pipe(select(FactoryShiftSelectors.getFactoryShiftInfo));
  }

  getFactoryHolidayInfo() {
    return this.store.pipe(select(FactoryShiftSelectors.getFactoryHolidayInfo));
  }

  getShiftCategoryTab() {
    return this.store.pipe(select(FactoryShiftSelectors.getShiftCategoryTab));
  }
}

export type TGetFactoryShiftInfo = ReturnType<FactoryShiftFacade['getFactoryShiftInfo']>;
export type TGetFactoryHolidayInfo = ReturnType<FactoryShiftFacade['getFactoryHolidayInfo']>;
export type TGetShiftCategoryTab = ReturnType<FactoryShiftFacade['getShiftCategoryTab']>;
export type TGetOrgHierarchy = ReturnType<FactoryShiftFacade['getOrgHierarcy']>;
